import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Search, MapPin, CalendarDays, Wallet, Heart, SlidersHorizontal, Radar, X, Bookmark, Music, ChevronRight, Sparkles, FileText, CalendarClock, CheckCircle2, AlertTriangle, Clock, LocateFixed, Loader2 } from "lucide-react";
import { api, apiError, money, formatDate, STATUS_META } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Slider } from "@/components/ui/slider";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";

const EVENT_TYPES = ["Show", "Ensaio", "Festival", "Culto", "Casamento", "Bar/Restaurante", "Corporativo", "Aniversário", "Outro"];
const GENRES = ["MPB", "Pop/Rock", "Sertanejo", "Gospel", "Samba/Pagode", "Jazz/Blues", "Forró", "Eletrônica", "Clássica"];
const EMPTY = { instrument: "", q: "", date_from: "", date_to: "", min_cache: "", max_distance: "", event_type: "", genre: "", only_available: true, only_eligible: true };

const TABS = [
  { id: "compat", label: "Compatíveis", accent: "compatíveis", icon: Sparkles },
  { id: "applied", label: "Candidatados", accent: "candidatados", icon: FileText },
  { id: "upcoming", label: "Próximos", accent: "próximos", icon: CalendarClock },
  { id: "fav", label: "Favoritos", accent: "favoritos", icon: Heart },
];

const relTime = (target) => {
  const abs = Math.abs(new Date(target) - new Date());
  const d = Math.floor(abs / 86400000), h = Math.floor((abs % 86400000) / 3600000), m = Math.floor((abs % 3600000) / 60000);
  if (d > 0) return `${d}d ${h}h`;
  if (h > 0) return `${h}h ${m}min`;
  return `${Math.max(m, 1)}min`;
};
const applyWindow = (e) => {
  const now = new Date();
  if (e.apply_open && new Date(e.apply_open) > now) return { tone: "amber", text: `Candidaturas abrem em ${relTime(e.apply_open)}`, blocked: true };
  if (e.apply_deadline) {
    const dl = new Date(e.apply_deadline);
    if (dl > now) return { tone: "green", text: `Candidaturas encerram em ${relTime(e.apply_deadline)}` };
    return { tone: "red", text: "Candidaturas encerradas", blocked: true };
  }
  return null;
};
const WIN_CLS = { green: "border-emerald-500/30 text-emerald-400 bg-emerald-500/10", amber: "border-amber-500/30 text-amber-400 bg-amber-500/10", red: "border-rose-500/30 text-rose-400 bg-rose-500/10" };

const PLACEHOLDERS = [
  "https://images.unsplash.com/photo-1493225457124-a3eb161ffa5f?crop=entropy&cs=srgb&fm=jpg&q=80&w=400",
  "https://images.unsplash.com/photo-1501386761578-eac5c94b800a?crop=entropy&cs=srgb&fm=jpg&q=80&w=400",
  "https://images.unsplash.com/photo-1470229722913-7c0e2dbbafd3?crop=entropy&cs=srgb&fm=jpg&q=80&w=400",
];
const coverFor = (e) => e.image || PLACEHOLDERS[(e.id || "").charCodeAt(0) % PLACEHOLDERS.length] || PLACEHOLDERS[0];
const fmtDrive = (m) => { if (m == null) return null; const h = Math.floor(m / 60), mm = m % 60; return h > 0 ? `${h}h${mm ? " " + mm + "min" : ""}` : `${mm}min`; };

function EventResultCard({ e, onFav, onOpen }) {
  const dist = e.compat?.distance_km;
  const price = e.eligible_cache ?? e.cache_total ?? e.cache ?? 0;
  const tags = ["Pago", e.type, ...(e.genres || [])].filter(Boolean).slice(0, 4);
  const meta = e.my_status ? STATUS_META[e.my_status] : null;
  const instr = (e.instruments || []).join(", ");
  const win = applyWindow(e);
  return (
    <div data-testid={`event-result-${e.id}`}
      className="group rounded-2xl border border-white/[0.07] bg-card overflow-hidden hover:border-accent/40 transition-colors">
      <div className="flex gap-3 p-3">
        <div className="relative w-24 sm:w-28 shrink-0 self-stretch rounded-xl overflow-hidden bg-[#1a1a20] min-h-[104px]"
          style={{ backgroundImage: `linear-gradient(to top, rgba(9,9,11,0.55), rgba(9,9,11,0.05)), url(${coverFor(e)})`, backgroundSize: "cover", backgroundPosition: "center" }}>
          {e.type && <span className="absolute top-2 left-2 text-[10px] font-bold px-2 py-0.5 rounded-full bg-[#FF4D00] text-white">{e.type}</span>}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-start gap-2">
            <button data-testid={`open-event-${e.id}`} onClick={() => onOpen(e)} className="min-w-0 text-left flex-1">
              <h3 className="font-head font-bold text-base sm:text-lg leading-tight truncate group-hover:text-accent transition-colors">{e.name}</h3>
            </button>
            {dist != null && (
              <span title="Distância aproximada em linha reta" className="flex items-center gap-1 text-[11px] text-zinc-300 bg-black/40 border border-white/10 rounded-full px-2 py-0.5 shrink-0">
                <MapPin className="h-3 w-3 text-accent" aria-hidden="true" />{dist} km
              </span>
            )}
            <button data-testid={`fav-${e.id}`} onClick={() => onFav(e)} title="Favoritar"
              className={`shrink-0 p-1.5 rounded-full border transition-colors ${e.is_favorite ? "border-rose-500/50 text-rose-400 bg-rose-500/10" : "border-white/10 text-zinc-400 hover:text-rose-400 hover:border-rose-500/40"}`}>
              <Heart className={`h-4 w-4 ${e.is_favorite ? "fill-current" : ""}`} />
            </button>
          </div>
          <div className="mt-1.5 space-y-1 text-xs text-zinc-400">
            <div className="flex items-center gap-1.5"><CalendarDays className="h-3.5 w-3.5 text-accent/80 shrink-0" /><span>{formatDate(e.date)} · {e.start_time}</span></div>
            <div className="flex items-center gap-1.5 min-w-0"><MapPin className="h-3.5 w-3.5 text-accent/80 shrink-0" /><span className="truncate">{e.address || "Local a definir"}</span></div>
            {instr && <div className="flex items-center gap-1.5 min-w-0"><Music className="h-3.5 w-3.5 text-accent/80 shrink-0" /><span className="truncate">{instr}</span></div>}
            {e.compat?.travel_min != null && (
              <div className="flex items-center gap-1.5 min-w-0" title="Tempo estimado de deslocamento de carro (estimativa)">
                <Clock className="h-3.5 w-3.5 text-accent/80 shrink-0" aria-hidden="true" />
                <span className="truncate">~{fmtDrive(e.compat.travel_min)} de carro · estimativa</span>
              </div>
            )}
          </div>
          <div className="mt-2 flex flex-wrap gap-1.5">
            {tags.map((t, i) => (
              <span key={i} className="text-[10px] font-medium px-2 py-0.5 rounded-full border border-accent/30 text-accent/90">{t}</span>
            ))}
            {e.available === false && (
              <span className="text-[10px] font-medium px-2 py-0.5 rounded-full border border-amber-500/40 text-amber-400 flex items-center gap-1"><AlertTriangle className="h-3 w-3" />Conflito</span>
            )}
          </div>
        </div>
      </div>
      {win && (
        <div data-testid={`apply-window-${e.id}`} className={`mx-3 mb-2 flex items-center gap-1.5 text-[11px] font-semibold px-3 py-1.5 rounded-lg border ${WIN_CLS[win.tone]}`}>
          <Clock className="h-3.5 w-3.5 shrink-0" />{win.text}
        </div>
      )}
      <div className="flex items-center justify-between gap-3 px-3 pb-3 pt-1 border-t border-white/[0.05]">
        <div className="min-w-0">
          <div className="font-mono font-bold text-accent leading-none" data-testid={`price-${e.id}`}>{money(price)}</div>
          <div className="text-[10px] text-zinc-500 mt-0.5">por músico</div>
        </div>
        {meta ? (
          <span data-testid={`status-${e.id}`} className={`text-xs px-3 py-2 rounded-xl border font-semibold ${meta.cls}`}>{meta.label.split("—")[0].trim()}</span>
        ) : win?.blocked ? (
          <span data-testid={`apply-blocked-${e.id}`} className="text-xs px-3 py-2 rounded-xl border border-white/10 text-zinc-500 font-semibold">{win.tone === "red" ? "Encerradas" : "Em breve"}</span>
        ) : (
          <Button data-testid={`apply-${e.id}`} onClick={() => onOpen(e)}
            className="rounded-xl bg-gradient-to-r from-[#FF4D00] to-accent text-white font-semibold gap-1 shadow-[0_0_16px_rgba(255,79,0,0.28)]">
            Candidatar-se<ChevronRight className="h-4 w-4" />
          </Button>
        )}
      </div>
    </div>
  );
}

export default function SearchEvents() {
  const { user, hasFeature, loadUser } = useAuth();
  const navigate = useNavigate();
  const myInstruments = user?.instruments || [];
  const hasLocation = user?.lat != null && user?.lng != null;
  const advanced = hasFeature("advanced_search");
  const canSave = hasFeature("saved_searches");
  const [tab, setTab] = useState("compat");
  const [f, setF] = useState(EMPTY);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [radar, setRadar] = useState({ on: false, radius: 50 });
  const [saved, setSaved] = useState([]);
  const [saveName, setSaveName] = useState("");
  const [geoLoading, setGeoLoading] = useState(false);

  const activeTab = TABS.find((t) => t.id === tab) || TABS[0];

  const useMyLocation = () => {
    if (!("geolocation" in navigator)) { toast.error("Seu navegador não suporta geolocalização."); return; }
    setGeoLoading(true);
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        try {
          await api.put("/profile", { lat: pos.coords.latitude, lng: pos.coords.longitude });
          await loadUser();
          const nr = { ...radar, on: true };
          setRadar(nr);
          fetchData(null, nr);
          toast.success("Localização definida! Radar ativado.");
        } catch (e) { toast.error("Não foi possível salvar sua localização. Tente novamente."); }
        finally { setGeoLoading(false); }
      },
      (err) => {
        setGeoLoading(false);
        if (err.code === 1) toast.error("Permissão de localização negada. Ative no navegador ou defina no perfil.");
        else toast.error("Não foi possível obter sua localização. Tente novamente ou defina no perfil.");
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 60000 }
    );
  };

  const fetchData = useCallback(async (override, radarOverride) => {
    setLoading(true);
    try {
      if (tab === "compat") {
        const src = override || f;
        const rd = radarOverride || radar;
        const params = {};
        Object.entries(src).forEach(([k, v]) => { if (v !== "" && v !== false) params[k] = v; });
        if (rd.on) params.radius_km = rd.radius;
        const { data } = await api.get("/events/search", { params });
        setRows(data);
      } else if (tab === "fav") {
        const { data } = await api.get("/favorites");
        setRows(data);
      } else {
        const { data } = await api.get("/events", { params: { scope: tab } });
        setRows(data);
      }
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  }, [tab, f, radar]);

  const loadSaved = useCallback(() => {
    if (!canSave) return;
    api.get("/saved-searches").then((r) => setSaved(r.data)).catch(() => {});
  }, [canSave]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { fetchData(); }, [tab]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { loadSaved(); }, []);

  const applyFilters = () => { setFiltersOpen(false); if (tab !== "compat") setTab("compat"); else fetchData(); };
  const clearFilters = () => { setF(EMPTY); setRadar({ on: false, radius: 50 }); };
  const activeFilterCount = Object.entries(f).filter(([k, v]) => k !== "only_eligible" && k !== "only_available" && v !== "" && v !== false).length + (radar.on ? 1 : 0);

  const toggleFav = async (e) => {
    const id = e.id; const cur = e.is_favorite;
    setRows((rs) => (tab === "fav" && cur ? rs.filter((r) => r.id !== id) : rs.map((r) => (r.id === id ? { ...r, is_favorite: !cur } : r))));
    try { cur ? await api.delete(`/events/${id}/favorite`) : await api.post(`/events/${id}/favorite`); }
    catch { toast.error("Não foi possível atualizar o favorito."); fetchData(); }
  };

  const saveSearch = async () => {
    if (!saveName.trim()) { toast.error("Dê um nome para a busca."); return; }
    try {
      await api.post("/saved-searches", { name: saveName.trim(), filters: f });
      toast.success("Busca salva."); setSaveName(""); loadSaved();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const applySaved = (s) => { const nf = { ...EMPTY, ...(s.filters || {}) }; setF(nf); setFiltersOpen(false); setTab("compat"); fetchData(nf); };
  const deleteSaved = async (id) => { try { await api.delete(`/saved-searches/${id}`); loadSaved(); } catch (e) { toast.error(apiError(e.response?.data?.detail)); } };

  const inputCls = "bg-[#09090B] border-white/10 rounded-xl mt-1.5";
  const emptyMsg = tab === "applied" ? "Você ainda não se candidatou a nenhum evento." : tab === "upcoming" ? "Nenhum evento confirmado por vir." : tab === "fav" ? "Você ainda não favoritou nenhum evento." : "Nenhum evento encontrado. Ajuste os filtros ou o raio do radar.";

  return (
    <div className="space-y-5 max-w-3xl mx-auto" data-testid="search-events-page">
      {/* HEADER */}
      <div>
        <h1 className="font-head text-4xl sm:text-5xl font-extrabold tracking-tight leading-[1.05]">
          Eventos <span className="bg-gradient-to-r from-[#FF4D00] to-accent bg-clip-text text-transparent">{activeTab.accent}</span>
        </h1>
        <p className="text-zinc-400 mt-2 text-sm">Oportunidades que combinam com o seu perfil musical, agenda e cachê.</p>
      </div>

      {/* SEARCH BAR */}
      <div className="flex items-center gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-zinc-500" />
          <Input data-testid="search-input" value={f.q} onChange={(e) => setF({ ...f, q: e.target.value })}
            onKeyDown={(e) => { if (e.key === "Enter") { if (tab !== "compat") setTab("compat"); else fetchData(); } }}
            placeholder="Buscar por evento, cidade, banda..."
            className="h-12 pl-12 pr-4 bg-card border-white/10 rounded-2xl text-base" />
        </div>
        <Sheet open={filtersOpen} onOpenChange={setFiltersOpen}>
          <SheetTrigger asChild>
            <button data-testid="open-filters-btn" className="relative h-12 w-12 shrink-0 rounded-2xl border border-white/10 bg-card flex items-center justify-center text-zinc-300 hover:border-accent/50 hover:text-accent transition-colors">
              <SlidersHorizontal className="h-5 w-5" />
              {activeFilterCount > 0 && <span className="absolute -top-1.5 -right-1.5 h-5 w-5 rounded-full bg-[#FF4D00] text-white text-[10px] font-bold flex items-center justify-center">{activeFilterCount}</span>}
            </button>
          </SheetTrigger>
          <SheetContent side="right" className="bg-[#0d0d11] border-white/10 w-full sm:max-w-md overflow-y-auto" data-testid="filters-drawer">
            <SheetHeader><SheetTitle className="font-head text-2xl flex items-center gap-2"><SlidersHorizontal className="h-5 w-5 text-accent" />Filtros</SheetTitle></SheetHeader>
            <div className="mt-6 space-y-5">
              <div>
                <Label className="text-xs text-zinc-500">Instrumento / função</Label>
                <Select value={f.instrument || "all"} onValueChange={(v) => setF({ ...f, instrument: v === "all" ? "" : v })}>
                  <SelectTrigger data-testid="filter-instrument" className={inputCls}><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[#121217] border-white/10"><SelectItem value="all">Todos</SelectItem>{myInstruments.map((i) => <SelectItem key={i} value={i}>{i}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs text-zinc-500">Tipo</Label>
                  <Select value={f.event_type || "all"} onValueChange={(v) => setF({ ...f, event_type: v === "all" ? "" : v })}>
                    <SelectTrigger data-testid="filter-type" className={inputCls}><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-[#121217] border-white/10"><SelectItem value="all">Todos</SelectItem>{EVENT_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs text-zinc-500">Gênero</Label>
                  <Select value={f.genre || "all"} onValueChange={(v) => setF({ ...f, genre: v === "all" ? "" : v })}>
                    <SelectTrigger data-testid="filter-genre" className={inputCls}><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-[#121217] border-white/10"><SelectItem value="all">Todos</SelectItem>{GENRES.map((g) => <SelectItem key={g} value={g}>{g}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="text-xs text-zinc-500">De (data)</Label><Input data-testid="filter-date-from" type="date" value={f.date_from} onChange={(e) => setF({ ...f, date_from: e.target.value })} className={inputCls} /></div>
                <div><Label className="text-xs text-zinc-500">Até (data)</Label><Input data-testid="filter-date-to" type="date" value={f.date_to} onChange={(e) => setF({ ...f, date_to: e.target.value })} className={inputCls} /></div>
              </div>
              {advanced && (
                <div className="grid grid-cols-2 gap-3">
                  <div><Label className="text-xs text-zinc-500">Cachê mínimo (R$)</Label><Input data-testid="filter-min-cache" type="number" step="0.01" value={f.min_cache} onChange={(e) => setF({ ...f, min_cache: e.target.value })} className={inputCls} /></div>
                  <div><Label className="text-xs text-zinc-500">Distância máx. (km)</Label><Input data-testid="filter-max-distance" type="number" value={f.max_distance} onChange={(e) => setF({ ...f, max_distance: e.target.value })} className={inputCls} /></div>
                </div>
              )}
              <div className="space-y-3 border-t border-white/[0.06] pt-4">
                {advanced && <label className="flex items-center gap-2 text-sm text-zinc-300"><Switch data-testid="filter-only-available" checked={f.only_available} onCheckedChange={(v) => setF({ ...f, only_available: v })} />Somente disponíveis (sem conflito)</label>}
                <label className="flex items-center gap-2 text-sm text-zinc-300"><Switch data-testid="filter-only-eligible" checked={f.only_eligible} onCheckedChange={(v) => setF({ ...f, only_eligible: v })} />Somente compatíveis com meu perfil</label>
              </div>

              {canSave && (
                <div className="border-t border-white/[0.06] pt-4 space-y-2" data-testid="saved-searches">
                  <Label className="text-xs text-zinc-500 flex items-center gap-1.5"><Bookmark className="h-3.5 w-3.5" />Buscas salvas</Label>
                  <div className="flex gap-2">
                    <Input data-testid="save-search-name" value={saveName} onChange={(e) => setSaveName(e.target.value)} placeholder="Nome da busca" className="bg-[#09090B] border-white/10 rounded-xl h-9" />
                    <Button data-testid="save-search-btn" onClick={saveSearch} variant="outline" size="sm" className="rounded-xl bg-transparent border-accent/40 text-accent gap-1 h-9 shrink-0"><Bookmark className="h-4 w-4" />Salvar</Button>
                  </div>
                  {saved.length > 0 && (
                    <div className="flex flex-wrap gap-2 pt-1">
                      {saved.map((s) => (
                        <span key={s.id} data-testid={`saved-search-${s.id}`} className="flex items-center gap-1 text-xs bg-[#09090B] border border-white/10 rounded-full pl-3 pr-1.5 py-1">
                          <button onClick={() => applySaved(s)} className="text-zinc-200 hover:text-accent">{s.name}</button>
                          <button data-testid={`del-saved-${s.id}`} onClick={() => deleteSaved(s.id)} className="text-zinc-500 hover:text-red-400"><X className="h-3.5 w-3.5" /></button>
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              )}

              <div className="flex gap-2 pt-2">
                <Button data-testid="clear-filters-btn" onClick={clearFilters} variant="outline" className="flex-1 rounded-xl bg-transparent border-white/10">Limpar</Button>
                <Button data-testid="apply-filters-btn" onClick={applyFilters} className="flex-1 rounded-xl bg-[#FF4D00] hover:bg-[#E64500]">Aplicar</Button>
              </div>
            </div>
          </SheetContent>
        </Sheet>
      </div>

      {/* TABS */}
      <div className="flex items-center gap-2 overflow-x-auto pb-1 -mx-1 px-1" data-testid="event-tabs">
        {TABS.map((t) => {
          const Icon = t.icon; const on = tab === t.id;
          return (
            <button key={t.id} data-testid={`tab-${t.id}`} onClick={() => setTab(t.id)}
              className={`flex items-center gap-2 px-4 h-11 rounded-2xl text-sm font-semibold whitespace-nowrap border transition-colors ${on ? "bg-gradient-to-r from-[#FF4D00] to-accent text-white border-transparent shadow-[0_0_16px_rgba(255,79,0,0.25)]" : "bg-card border-white/10 text-zinc-400 hover:text-zinc-200 hover:border-white/25"}`}>
              <Icon className="h-4 w-4" />{t.label}
            </button>
          );
        })}
      </div>

      {/* RADAR (only compat) */}
      {tab === "compat" && (
        <div className="rounded-2xl border border-white/[0.07] bg-card p-4" data-testid="radar-panel">
          <div className="flex items-center justify-between">
            <label className="flex items-center gap-2.5 text-sm font-semibold text-zinc-200">
              <span className={`h-9 w-9 rounded-xl flex items-center justify-center ${radar.on ? "bg-accent/15 text-accent" : "bg-white/5 text-zinc-500"}`}><Radar className="h-5 w-5" /></span>
              Radar — eventos perto de mim
            </label>
            <Switch data-testid="radar-toggle" checked={radar.on} disabled={!hasLocation}
              onCheckedChange={(v) => { if (!hasLocation) return; const nr = { ...radar, on: v }; setRadar(nr); if (tab === "compat") fetchData(null, nr); }} />
          </div>
          {!hasLocation ? (
            <div data-testid="radar-no-location" className="mt-3 rounded-xl border border-amber-500/25 bg-amber-500/5 p-3">
              <div className="flex items-start gap-2.5">
                <MapPin className="h-4 w-4 text-amber-400 shrink-0 mt-0.5" aria-hidden="true" />
                <p className="text-xs text-amber-300/90 leading-relaxed">Ative sua localização para o radar mostrar os eventos mais próximos e calcular as distâncias.</p>
              </div>
              <div className="mt-3 flex flex-wrap items-center gap-2">
                <Button data-testid="radar-use-location" onClick={useMyLocation} disabled={geoLoading} aria-busy={geoLoading}
                  aria-label="Usar minha localização atual para ativar o radar"
                  className="h-9 rounded-xl bg-gradient-to-r from-[#FF4D00] to-accent text-white font-semibold gap-1.5 text-xs">
                  {geoLoading ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> : <LocateFixed className="h-4 w-4" aria-hidden="true" />}
                  {geoLoading ? "Obtendo localização…" : "Usar minha localização atual"}
                </Button>
                <button data-testid="radar-complete-location" onClick={() => navigate("/completar-perfil?step=1")}
                  className="inline-flex items-center gap-1 text-xs font-semibold text-zinc-400 hover:text-accent">
                  Definir no perfil<ChevronRight className="h-3.5 w-3.5" aria-hidden="true" />
                </button>
              </div>
            </div>
          ) : (
            <div className="mt-4">
              {radar.on && (
                <>
                  <div className="flex items-center justify-between text-xs text-zinc-400 mb-2">
                    <span>Distância máxima do radar</span>
                    <span className="font-mono font-bold text-accent" data-testid="radar-value">{radar.radius} km</span>
                  </div>
                  <Slider data-testid="radar-slider" value={[radar.radius]} min={5} max={500} step={5}
                    onValueChange={(v) => setRadar((r) => ({ ...r, radius: v[0] }))}
                    onValueCommit={(v) => fetchData(null, { on: true, radius: v[0] })} />
                </>
              )}
              <button data-testid="radar-update-location" onClick={useMyLocation} disabled={geoLoading} aria-busy={geoLoading}
                className="mt-3 inline-flex items-center gap-1.5 text-xs font-medium text-zinc-400 hover:text-accent disabled:opacity-50">
                {geoLoading ? <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" /> : <LocateFixed className="h-3.5 w-3.5" aria-hidden="true" />}
                {geoLoading ? "Atualizando…" : "Atualizar minha localização atual"}
              </button>
            </div>
          )}
        </div>
      )}

      {/* RESULTS */}
      <div className="space-y-3" data-testid="search-events-results">
        {loading ? (
          [0, 1, 2].map((i) => <div key={i} className="h-36 rounded-2xl bg-card border border-white/[0.05] animate-pulse" />)
        ) : rows.length === 0 ? (
          <div className="text-center py-16" data-testid="empty-state">
            <div className="h-14 w-14 rounded-2xl bg-white/5 flex items-center justify-center mx-auto mb-3"><Search className="h-7 w-7 text-zinc-600" /></div>
            <p className="text-sm text-zinc-500">{emptyMsg}</p>
          </div>
        ) : (
          rows.map((e) => <EventResultCard key={e.id} e={e} onFav={toggleFav} onOpen={(ev) => navigate(`/eventos/${ev.id}`)} />)
        )}
      </div>
    </div>
  );
}
